import * as React from "react";

import { Slot } from "@radix-ui/react-slot";
import { type VariantProps, cva } from "class-variance-authority";

import { ReactComponent as LoadingIcon } from "@assets/svg/loading-spinner.svg";

import { cn } from "../../utils/ui";

const buttonVariants = cva(
  cn(
    // Montserrat semibold, not bold — every button is an interactive label
    // (design: Typefaces).
    "inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg font-head font-semibold transition-all outline-none",
    "disabled:cursor-not-allowed",
    "[&_svg]:pointer-events-none [&_svg:not([class*='size-'])]:size-4 shrink-0 [&_svg]:shrink-0",
    "focus-visible:border-ring focus-visible:ring-ring/50 focus-visible:ring-[3px]",
    "aria-invalid:ring-destructive/20 aria-invalid:border-destructive"
  ),
  {
    variants: {
      variant: {
        // Primary: navy, per the design (Claude Design "Report Studio UI
        // Update" / Main Pages (Half-Dark).dc.html --s-primary/--s-primary-hover).
        // Disabled dims via opacity (not a lower-alpha bg) so text/bg contrast
        // doesn't depend on whatever page background shows through.
        default: cn(
          "border-2 border-primary bg-primary text-primary-foreground hover:border-primary-hover hover:bg-primary-hover",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        // Solid blue — the brand blue (secondary) as a filled CTA, e.g. the
        // Landing Continue/Login button and any action that needs to read as
        // "blue" rather than navy (default) at a glance.
        secondary: cn(
          "border-2 border-secondary bg-secondary text-secondary-foreground hover:border-secondary-600 hover:bg-secondary-600",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        // The plain blue "outline" variant (transparent bg) was removed —
        // the two outline-family styles going forward are simple-outline
        // (neutral, for Cancel/Preview/dismiss actions) and secondary-subtle
        // (blue tinted, for genuine secondary CTAs like Report QA/Download
        // PDF). Don't re-add a bare `outline` key.
        "simple-outline": cn(
          "border-2 border-border bg-transparent text-foreground",
          "hover:bg-secondary/20",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        // Used on the dark navy Landing hero — needs a light BLUE tint to
        // read against a dark navy background, not a light navy tint (which
        // would be nearly invisible against navy-on-navy).
        "dark-outline": cn(
          "border-2 border-secondary-100 bg-transparent text-secondary-100",
          "hover:bg-foreground-dark/10",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        // Soft/tinted secondary — same treatment as the deprecated
        // accent-subtle, blue instead of purple. border-2 (not the
        // inherited border-1) to match every other variant's border weight.
        "secondary-subtle": cn(
          "border-2 border-secondary-200 bg-secondary-50 text-secondary",
          "hover:bg-secondary-100 hover:text-secondary",
          "disabled:bg-secondary-50 disabled:text-secondary-300 disabled:border-secondary-100"
        ),
        destructive:
          "bg-destructive text-destructive-foreground hover:bg-destructive-700",
        "destructive-outline": cn(
          "border-2 border-destructive bg-destructive-50 text-destructive",
          "hover:bg-destructive/20 hover:text-destructive",
          "disabled:bg-destructive-100 disabled:text-destructive-foreground disabled:border-none"
        ),
        // Muted at rest, red pill on hover — for a destructive row action
        // that shouldn't compete with the row's other buttons until the user
        // actually reaches for it (e.g. AccessControl's "Remove").
        "destructive-ghost": cn(
          "border-2 border-transparent text-foreground-muted",
          "hover:bg-destructive-50 hover:text-destructive",
          "disabled:text-foreground-muted/50"
        ),
        // Green outline counterpart to destructive-outline — same shape, for
        // a positive/reactivating action (e.g. AccessControl's "Activate").
        "success-outline": cn(
          "border-2 border-success-600 bg-transparent text-success-800",
          "hover:bg-success-50 hover:text-success-800",
          "disabled:opacity-50 disabled:pointer-events-none"
        ),
        // Deprecated `accent`/`accent-outline`/`accent-subtle` (purple)
        // variants removed — zero consumers left (all migrated to
        // secondary/secondary-subtle). Purple isn't part of the theme; don't
        // re-add a purple Button variant.
        ghost: "hover:bg-muted hover:text-foreground",
        // Link-style text — primary-ink (blue), not navy, so it reads as a
        // link affordance rather than as body text.
        link: "text-primary-ink hover:text-primary-ink-hover underline-offset-4 hover:underline",
      },
      size: {
        default: "px-6 py-3 gap-2 text-sm",
        sm: "text-xs gap-1.5 px-5 py-2.5",
        compact:
          "h-8 text-[11px] gap-1.5 px-3 py-1 [&_svg:not([class*='size-'])]:size-3.5",
        icon: "size-12",
      },
    },
    defaultVariants: {
      variant: "default",
      size: "default",
    },
  }
);

const Button = React.forwardRef<
  HTMLButtonElement,
  React.ComponentProps<"button"> &
    VariantProps<typeof buttonVariants> & {
      asChild?: boolean;
      isLoading?: boolean;
    }
>(
  (
    { className, variant, size, asChild = false, isLoading = false, ...props },
    ref
  ) => {
    const Comp = asChild ? Slot : "button";

    return (
      <Comp
        ref={ref}
        data-slot="button"
        className={cn(buttonVariants({ variant, size, className }))}
        {...props}
        disabled={isLoading || props.disabled}
      >
        {isLoading && <LoadingIcon className="size-4 animate-spin-ccw" />}

        {props.children}
      </Comp>
    );
  }
);
Button.displayName = "Button";

export { Button, buttonVariants };
